import { useEffect, useState } from "react";
import { Youtube, Instagram, Facebook, Music2, Share2, HardDrive, Clock, Download, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { CinematicStage, StageHeader, GlassCard, Pill, EmptyState, Spinner, cx } from "@/components/shared";

const PLATFORM_ICON = {
  "YouTube": Youtube, "YouTube Shorts": Youtube, "Instagram Reels": Instagram,
  "Facebook Reels": Facebook, "TikTok": Music2,
};

export default function ExportCenter() {
  const [projects, setProjects] = useState(null);
  const [selected, setSelected] = useState(null);
  const [exports, setExports] = useState(null);

  useEffect(() => {
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      const ready = data.find((p) => p.status === "ready") || data[0];
      if (ready) pick(ready);
    }).catch((e) => { console.error("[ExportCenter] failed to load projects", e); setProjects([]); });
  }, []);

  const pick = async (p) => {
    setSelected(p);
    setExports(null);
    try { const { data } = await api.get(`/projects/${p.id}/exports`); setExports(data); }
    catch (e) { console.error("[ExportCenter] failed to load exports for project", p.id, e); }
  };

  if (!projects) return <Spinner label="Loading Export Center…" />;

  return (
    <CinematicStage>
      <StageHeader stage="17" room="The Delivery Bay · Export" icon={Share2} title="Ship to every platform"
        subtitle="Simultaneous multi-platform exports, intelligently composed — not cropped." />

      {projects.length === 0 ? (
        <EmptyState icon={Share2} title="No projects to export" subtitle="Produce a project first, then export it everywhere." />
      ) : (
        <div className="grid lg:grid-cols-4 gap-6">
          <GlassCard className="p-4 lg:col-span-1 self-start">
            <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-3 px-2">Projects</div>
            <div className="space-y-1">
              {projects.map((p) => (
                <button key={p.id} onClick={() => pick(p)} data-testid={`export-project-${p.id}`}
                  className={cx("w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left text-sm transition", selected?.id === p.id ? "bg-[#5B4DFF]/25 text-white border border-[#5B4DFF]/40" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent")}>
                  <span className="flex-1 truncate">{p.title}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              ))}
            </div>
          </GlassCard>

          <div className="lg:col-span-3">
            {!exports ? <Spinner /> : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-semibold">{exports.project}</h2>
                  <Pill tone={exports.video_provider ? "green" : "slate"}>{exports.video_provider ? "Renderer ready" : "Awaiting video provider"}</Pill>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {exports.exports.map((e) => {
                    const Icon = PLATFORM_ICON[e.platform] || Share2;
                    return (
                      <GlassCard key={e.platform} hover className={cx("p-5", e.future && "opacity-70")} data-testid="export-card">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/5 grid place-items-center"><Icon className="w-5 h-5 text-[#b6aaff]" /></div>
                            <div>
                              <div className="text-white font-medium">{e.platform}</div>
                              <div className="text-xs text-slate-500">{e.aspect} · {e.resolution}</div>
                            </div>
                          </div>
                          {e.future && <Pill tone="slate">Future</Pill>}
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                          <Meta icon={HardDrive} label="Est. size" value={`${e.estimated_size_mb} MB`} />
                          <Meta icon={Clock} label="Est. time" value={`~${e.estimated_time_seconds}s`} />
                        </div>
                        <button disabled={e.status !== "ready_to_render"} data-testid={`export-render-${e.platform}`}
                          className={cx("w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium",
                            e.status === "ready_to_render" ? "bg-[#5B4DFF] text-white" : "bg-white/5 border border-white/10 text-slate-500")}>
                          <Download className="w-4 h-4" /> {e.status === "ready_to_render" ? "Render & Export" : "Connect video provider"}
                        </button>
                      </GlassCard>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </CinematicStage>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-white mt-0.5">{value}</div>
    </div>
  );
}
